import {Grid, ListItem, List} from '@material-ui/core';
import 'Agent/AgentDocumentation/components/ProducerCompliancePolicy.sass';
import {
  clear,
  getProducerCompliancePolicyAttachments,
} from 'Agent/AgentDocumentation/ducks/producerCompliancePolicyAttachments';
import {matchAsync} from 'Core/models/Async';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import ErrorMessage from 'Shared/components/ErrorMessage';
import FileDownloadButton from 'Shared/components/FileDownloadButton';
import Loading from 'Shared/components/Loading';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';

const getFileNameWithoutExtension = (fileName: string) => fileName.slice(0, fileName.indexOf('.'));
const getAttachmentSortOrderByName = (attachmentName: string) => {
  switch (attachmentName) {
    case 'AHL Business Associate Addendum':
      return 1;
    case 'ALNY Business Associate Addendum':
      return 2;
    case 'AB Do Not Call Policy':
      return 3;
    case 'AB Internal Replacement Policy':
      return 4;
    case 'Allstate Anti-Money Laundering Communication':
      return 5;
    case 'AB Texting Policy':
      return 6;
    default:
      return 7;
  }
};

type ProducerCompliancePolicyProps = {
  selectedAgency: AgencyFilters;
};

const ProducerCompliancePolicy = (props: ProducerCompliancePolicyProps) => {
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const attachments = useSelector((state: ReducerState) => state.producerCompliancePolicyAttachments);

  useEffect(() => {
    dispatch(getProducerCompliancePolicyAttachments(selectedAgency.encryptedIdentifier));

    return () => {
      dispatch(clear());
    };
  }, [dispatch, selectedAgency.encryptedIdentifier]);

  return (
    <div className="producer-compliance-policy">
      <h1>Producer Compliance Policy</h1>
      <h2>Table of Contents</h2>
      <ul className="producer-compliance-policy__table-of-contents-list">
        <li>
          <a href="#introduction">Introduction</a>
        </li>
        <li>
          <a href="#ethics">Ethics</a>
        </li>
        <li>
          <a href="#producerAppointmentRequirements">Producer Appointment Requirements</a>
          <ul>
            <li>
              <a href="#licensing">Licensing</a>
            </li>
            <li>
              <a href="#criminalConvictions">Criminial Convictions</a>
            </li>
            <li>
              <a href="#antiFraudTraining">Anti-Fraud Training</a>
            </li>
            <li>
              <a href="#errorsAndOmissionsInsurance">Errors and Omissions Insurance</a>
            </li>
            <li>
              <a href="#noticeOfChangesToABPolicies">Notice of Changes to AB Policies</a>
            </li>
          </ul>
        </li>
        <li>
          <a href="#salesSolicitation">Sales Solicitation</a>
          <ul>
            <li>
              <a href="#presentationOfProductAndPremium">Presentation of Product and Premium</a>
            </li>
            <li>
              <a href="#representationsToApplicants">Representations to Applicants</a>
            </li>
            <li>
              <a href="#salesMaterial">Sales Material</a>
            </li>
            <li>
              <a href="#bestInterestStandard">Best Interest Standard</a>
            </li>
            <li>
              <a href="#takingTheApplicationForInsurance">Taking the Application For Insurance</a>
            </li>
            <li>
              <a href="#submittingTheApplication">Submitting the Application</a>
            </li>
            <li>
              <a href="#replacementOfInsurance">Replacement of Insurance</a>
            </li>
            <li>
              <a href="#crossBorderSales">Cross Border Sales (New York Resident Producers Only)</a>
            </li>
            <li>
              <a href="#targetMarket">Target Market</a>
            </li>
            <li>
              <a href="#erisa">ERISA</a>
            </li>
            <li>
              <a href="#salesToSeniors">Sales to Seniors</a>
            </li>
            <li>
              <a href="#directSalesGuidelines">Direct Sales Guidelines</a>
            </li>
          </ul>
          <li>
            <a href="#customerFunds">Customer Funds</a>
            <ul>
              <li>
                <a href="#premiumPayments">Premium Payments</a>
              </li>
              <li>
                <a href="#policyFunds">Policy Funds</a>
              </li>
            </ul>
          </li>
          <li>
            <a href="#policyService">Policy Service</a>
            <ul>
              <li>
                <a href="#policyDelivery">Policy Delivery</a>
              </li>
              <li>
                <a href="#policyholderCorrespondence">Policyholder Correspondence</a>
              </li>
              <li>
                <a href="#complaints">Complaints</a>
              </li>
              <li>
                <a href="#claims">Claims</a>
              </li>
            </ul>
          </li>
          <li>
            <a href="#compensation">Compensation</a>
            <ul>
              <li>
                <a href="#noRebating">No Rebating</a>
              </li>
              <li>
                <a href="#compensationCalculation">Compensation Calculation</a>
              </li>
              <li>
                <a href="#compensationOnPremiumRateIncreases">Compensation on Premium Rate Increases</a>
              </li>
              <li>
                <a href="#interestOnDebtsToAB">Interest on Debts to AB</a>
              </li>
              <li>
                <a href="#compensationOffsets">Compensation Offsets</a>
              </li>
            </ul>
          </li>
          <li>
            <a href="#allstateAndAllstateBenefitsNameAndLogo">Allstate and Allstate Benefits Name and Logo</a>
            <ul>
              <li>
                <a href="#general">General</a>
              </li>
              <li>
                <a href="#businessCardsAndLetterhead">Business Cards and Letterhead</a>
              </li>
              <li>
                <a href="#advertisingMarketingMaterial">Advertising/Marketing Material</a>
              </li>
            </ul>
          </li>
          <li>
            <a href="#privacyAndSecurityResponsibilities">Privacy and Security Responsibilities</a>
            <ul>
              <li>
                <a href="#grammLeachBlileyActOf1999">Gramm-Leach-Bliley Act of 1999 (GLB)</a>
              </li>
              <li>
                <a href="#healthInsurancePortabilityAndAccountabilityActOf1996">
                  Health Insurance Portability and Accountability Act of 1996 (HIPAA)
                </a>
              </li>
              <li>
                <a href="#producerResponsibilities">Producer Responsibilities</a>
              </li>
              <li>
                <a href="#storageOfPersonalInformation">Storage Of Personal Information</a>
              </li>
              <li>
                <a href="#recordingTelephoneConversations">Recording Telephone Conversations</a>
              </li>
              <li>
                <a href="#terminationOfEmploymentOfIndividualsWorkingOnBehalfOfAnAgency">
                  Termination of Employment of Individuals Working On Behalf of an Agency
                </a>
              </li>
              <li>
                <a href="#securityRequirementsForVendors">Security Requirements for Vendors</a>
              </li>
              <li>
                <a href="#newYorkCybersecurityRequirements">
                  New York Cybersecurity Requirements (New York Producers Only)
                </a>
              </li>
              <li>
                <a href="#usaPatriotAct">USA PATRIOT Act</a>
              </li>
              <li>
                <a href="#complianceWithCustomerAndProspectContactRulesAndRegulations">
                  Compliance with Customer and Prospect Contact Rules and Regulations
                </a>
              </li>
              <li>
                <a href="#textingPolicy">Texting Policy</a>
              </li>
              <li>
                <a href="#canSpamAct">CAN SPAM Act</a>
              </li>
              <li>
                <a href="#doNotCallRegistry">Do Not Call Registry</a>
              </li>
            </ul>
          </li>
        </li>
        <li>
          <a href="#violations">Violations</a>
        </li>
        <li>
          <a href="#commentsAndQuestions">Comments and Questions</a>
        </li>
        <li>
          <a href="#attachments">Attachments</a>
        </li>
      </ul>
      <h2 id="introduction">Introduction</h2>
      <p>
        This Producer Compliance Policy (“Policy”) supplements the producer/agent agreements of American Heritage Life
        Insurance Company (“AHL”) and Allstate Life Insurance Company of New York (“ALNY”), individually and
        collectively (“Allstate Benefits” or “AB”), pursuant to the provisions by which AB may amend or supplement the
        agreements with appropriate official written communication. All producers are required to remain informed of,
        and act in compliance with, the standards outlined in this Policy, and all applicable state and federal laws and
        regulations.
      </p>
      <p>
        Unless otherwise noted, this Policy applies to all Independent Agents (“IA”), R3001 Exclusive Agencies (“EA”)
        and all L2000 Exclusive Financial Specialists (“EFS”) appointed with:
      </p>
      <ul>
        <li>
          AHL pursuant to the Supplement to their respective Agreements. This Policy’s application, though, is limited
          to the representation of AHL and has no effect on policies or procedures governing the representation of other
          Allstate affiliates.
        </li>
        <li>
          ALNY pursuant to the Supplement to their respective Agreements only to the extent their representation
          includes products marketed under the AB marketing name and administered by the AB Service Center in
          Jacksonville, Florida.
        </li>
      </ul>
      <p>Throughout this Policy:</p>
      <ul>
        <li>“Allstate Benefits” and “AB” mean AHL and ALNY, individually and collectively.</li>
        <li>
          “You” and “producer” means the agent or agency appointed with AHL and/or ALNY and whose producer/agent
          agreement is supplemented by this Policy.
        </li>
        <li>“We”, “us”, and “our” means AB.</li>
      </ul>
      <h2 id="ethics">Ethics</h2>
      <p>
        As a member of the insurance industry, you must comply with the letter and the spirit of the laws and
        regulations that govern our industry. Allstate Benefits is committed to operating its business in an ethical
        manner. As an Allstate Benefits producer, you are expected to maintain the highest ethical standards. When
        acting on behalf of Allstate Benefits, producers are required to act in compliance with the company’s ethical
        standards and adhere to the Allstate Benefits agency standards. Producers are also solely responsible for their
        employees or others working on their behalf and must ensure those individuals comply with the Allstate Benefits
        standards, the company’s standards with respect to ethical business practices, and all federal, state and local
        laws and regulations.
      </p>
      <p>
        Commitment to exemplary market conduct performance is one of the specific driving forces at AB. The AB Market
        Conduct Policy, which is attached, sets forth the principles to which AB, its employees and its producers are
        committed.
      </p>
      <h2 id="producerAppointmentRequirements">Producer Appointment Requirements</h2>
      <h3 id="licensing">Licensing</h3>
      <p>
        AB seeks only those producers of good character and business repute who have the appropriate education,
        qualifications and training to represent AB.
      </p>
      <p>
        Before engaging in any form of solicitation of AB products, you must be licensed and appointed by AB in the
        applicable jurisdiction(s). Solicitation includes any form of advertising, such as providing sales literature to
        a prospect. Failure to be properly licensed and appointed usually precludes, by law, issuance of coverage and
        compensation.
      </p>
      <p>
        Although AB will assist you in acquiring the appropriate license and appointment, the responsibility for this
        remains with you. You must also maintain your license and satisfy all requirements for its renewal, including
        continuing education.
      </p>
      <p>Unlicensed agency staff may not:</p>
      <ul>
        <li>solicit for insurance,</li>
        <li>discuss or quote premiums,</li>
        <li>discuss or provide advice concerning any aspect of insurance coverages,</li>
        <li>
          engage in any sales-related activity beyond completing data entry or other clerical activity for a licensed
          producer,
        </li>
        <li>receive compensation based on sales, except as permitted by law, or</li>
        <li>be involved in any activity or transaction that is in violation of state licensing or other laws.</li>
      </ul>
      <h3 id="criminalConvictions">Criminal Convictions</h3>
      <p>
        To combat the costs of insurance fraud, Congress passed the Violent Crime Control and Law Enforcement Act of
        1994. Among its provisions, the statute prohibits a person from willfully engaging in the business of insurance
        if convicted of a criminal felony involving dishonesty or breach of trust. The statute further prohibits anyone
        from willfully permitting such a person to participate in the business of insurance. Although waivers are
        possible, only the insurance commissioner can grant them.
      </p>
      <p>
        AB strives to ensure compliance with all regulatory and legal requirements and vigorously seeks to detect and
        prevent insurance fraud. Our employees and producers are the first line of defense to assist AB in meeting these
        obligations and goals.
      </p>
      <p>
        To ensure compliance with this law, our producers are required to immediately notify AB, your Regional Office,
        if any of the following individuals have been convicted of a federal or state felony:
      </p>
      <ul>
        <li>Licensed producer;</li>
        <li>Employee of the agency;</li>
        <li>Officer, director, or other representative of the agency; or</li>
        <li>Enroller or other contractor of the agency who performs insurance-related services.</li>
      </ul>
      <p>
        If you or anyone in your agency as described above has ever been convicted of a felony, you must advise your
        Regional Office in writing so that corrective action can be taken. Your notice should include a description of
        the conviction, the court and date, and whether your state’s Insurance Department has provided you/the convicted
        person with the necessary waiver.
      </p>
      <h3 id="antiFraudTraining">Anti-Fraud Training</h3>
      <p>
        Some states require insurance producers to participate in anti-fraud training. AB provides anti-fraud training
        material through our agent website. All producers are required to review this material in compliance with that
        state’s requirement for insurer sponsored anti-fraud training.
      </p>
      <h3 id="errorsAndOmissionsInsurance">Errors and Omissions Insurance</h3>
      <p>
        You are required to maintain Errors and Omissions (“E&amp;O”) insurance in the minimum amount of $1 million per
        occurrence. This insurance must cover your conduct as an insurance producer for AB.
      </p>
      <p>
        Producers must provide proof of this E&amp;O coverage at time of appointment/contract for every person or entity
        to be appointed/contracted and must maintain this coverage while contracted with AB. Producers failing to
        maintain E&amp;O coverage are subject to having their appointments and contracts terminated.
      </p>
      <h3 id="noticeOfChangesToABPolicies">Notice of Changes to AB Policies</h3>
      <p>
        AB may amend or supplement your producer agreement by field bulletin, letter, email, publication on a website
        maintained by AB for insurance producers, or other appropriate official written communication. Your continuing
        relationship with AB after transmission of such official written communication conclusively constitutes assent
        thereto. You are required to provide to AB, and update as necessary, a current email address to which such
        official written communications may be sent. Failure to provide your current email address to AB constitutes a
        waiver of your right to notice of such official written communication.
      </p>
      <h2 id="salesSolicitation">Sales Soliciation</h2>
      <h3>Adherence to Underwriting Guidelines</h3>
      <p>
        State laws allow insurers to set appropriate underwriting standards, including type of underwriting (i.e.,
        Guaranteed Issue (“GI”), Contingent Guaranteed Issue (“CGI”), Simplified Issue ("SI"), Full Underwriting, etc.)
        which may include different underwriting criteria for different products, as long as the underwriting standards
        are based on sound underwriting and actuarial principles related to actual or reasonably anticipated loss
        experience and are not contrary to law. Adherence to our published underwriting guidelines helps insure we do
        not violate unfair discrimination laws.
      </p>
      <p>
        AB is committed to active, fair, and honest competition as best serving our customers. Toward that end, we
        provide our producers with competitive products and services and the tools to communicate the advantages and
        strengths of AB. We expect our producers to serve our customers in a fair, honest, and knowledgeable way that
        best satisfies the customers’ insurance needs and financial objectives.
      </p>
      <h3 id="presentationOfProductAndPremium">Presentation of Product &amp; Premium</h3>
      <p>
        <u>Product Presentation.</u> Tying or bundling is an arrangement to sell one product or service on the condition
        that the buyer also purchases an additional product or service. Producers must not improperly tie the sale of AB
        products to the sale of another product or service. Generally, customers may be offered the opportunity to
        purchase multiple products and services so long as they are not required to purchase an AB product to be offered
        another product or service, or vice versa.
      </p>
      <p>
        Many states prohibit use of specific words or terms when referring to certain insurance products. For example,
        some states prohibit the use of the words{' '}
        <strong>
          <em>bronze, silver, gold, platinum,</em>
        </strong>{' '}
        and the term{' '}
        <strong>
          <em>essential benefits</em>
        </strong>{' '}
        with supplemental health insurance products. Therefore, AB products may not be sold using any customized product
        name or term not approved by AB.
      </p>
      <p>
        <u>Premium Presentation.</u> In most states, premium for AB products is regulated by the state insurance
        department and must always be disclosed to the customer. No charges may be incorporated into the premium for AB
        products. Charges for other permissible products and services must be separately disclosed. Producers are
        prohibited from charging separate fees for the solicitation of insurance products or for existing insurance
        policies. These services may include, but are not limited to:
      </p>
      <ul>
        <li>mailing coverage documents,</li>
        <li>preparing correspondence and other clerical work, and</li>
        <li>assisting with claim submissions.</li>
      </ul>
      <h3 id="representationsToApplicants">Representations to Applicants</h3>
      <p>
        Special care must be exercised to ensure that your presentations are accurate, complete, and appropriate. All
        statements, both verbal and written, must comply with applicable laws and regulations. Remember to apply the
        Golden Rule in all your dealings with prospects and customers.
      </p>
      <p>
        <u>Always identify yourself.</u> Never misrepresent yourself or your qualifications. If you claim to be an
        expert in an area, you must possess the necessary qualifications and certifications as required by the
        applicable laws and regulations, and/or governing organizations.
      </p>
      <p>
        <u>Do what is best for the customer.</u> Recommend to customers only insurance products that you believe are in
        their best interest. Help customers identify their insurable needs or financial objectives and recommend
        products that help them meet those needs or objectives.
      </p>
      <p>
        <u>Know the products.</u> It is the producer’s responsibility to know and understand any product recommended and
        to relay this information to the client. Producers should be familiar with the product specifics pertinent to
        each client, such as costs, fees, expense structures, restrictions, potential benefits, and investment risks.
        Knowing the product, its benefits, limits, and conditions is key to helping the applicant meet their insurance
        needs. For example, if underwriting for a product is Guaranteed Issue, this does not mean that pre-existing
        conditions will be covered. Maintain a thorough working knowledge of all insurance products that you present to
        customers. Consult your Regional Office when in doubt and to acquire product information materials.
      </p>
      <p>
        <u>Be accurate in your presentation.</u> When discussing our products, explain in detail their terms and
        conditions accurately and fairly. Never permit a prospect to be misled.
      </p>
      <p>
        <u>Comply with legal requirements.</u> Keep informed of applicable laws and regulations and observe them in
        soliciting, selling, and servicing insurance.
      </p>
      <p>
        <u>Stay Positive.</u> AB encourages you to sell based on the strength and value of our products and service. Any
        statement, either written or oral, that is false, or maliciously critical of or derogatory to the financial
        condition of any insurer, and that is calculated to injure such insurer has no place in the selling of our
        products. Any statement about, or comparison regarding a competitor must be fair, honest, and complete. Do not
        engage in “competitor bashing” to promote any sale.
      </p>
      <p>
        <u>Take care with replacements.</u> Replacing existing insurance is often not in the best interests of the
        customer. Be sure you know whether the customer plans to replace existing coverage and, if so, ensure the
        customer is provided the information they need to make a knowledgeable decision.
      </p>
      <h3 id="salesMaterial">Sales Material</h3>
      <p>
        <u>Definition of Sales Material.</u> Sales material includes all printed and published material, audiovisual
        material, brochures, seminar presentations, direct mail, newspaper ads, magazines, sales scripts, radio and TV
        scripts, web sites, Internet or Intranet communications, email and texting, and sales aids of all kinds for
        presentation to members of the insurance-buying public intended to promote AB or any AB product.
      </p>
      <p>
        <u>Rules for content and use of sales material.</u> Producers may only use sales materials and techniques that
        are clear as to purpose and honest and fair as to content. Furthermore, sales material must be used consistent
        with the needs of the customer.
      </p>
      <p>
        <u>AB-created sales material is available.</u> All AB approved sales materials that can be ordered online,
        produced through a custom request specific to group enrollments, or for agency book of business requests, cannot
        be altered without prior approval from Allstate Benefits.
      </p>
      <p>
        <u>Producer and employer created sales material must be approved by AB.</u> All sales material not provided by
        AB must be approved by AB prior to use. You should submit this material to your Regional Office, so that they
        may coordinate a review and obtain necessary approvals from AB. Approved sales materials cannot be altered
        without approval.
      </p>
      <h3 id="bestInterestStandard">Best Interest Standard</h3>
      <p>
        Insurance recommendations are to be governed by the best interests of the customer rather than the potentially
        competing financial incentives of the producer. Producers must exercise care in their communications with
        customers and are required to operate using basic fiduciary norms and standards of fair dealing this includes,
        but not limited to, a duty to fairly and accurately describe recommended products and services.
      </p>
      <p>
        Producers must have reasonable grounds to support that a recommendation to purchase or exchange a life insurance
        or annuity product is in the best interest of the customer. Producers are required to:
      </p>
      <ul>
        <li>
          Comply with all state and company continuing education requirements, prior to soliciting any insurance
          business
        </li>
        <li>
          Make reasonable effort to obtain information containing the customer’s financial situation and objectives.
          These details, along with other relevant information, will provide a basis demonstrating that the
          recommendation is suitable
        </li>
        <li>Disclose all features of the recommended product</li>
        <li>
          Use sales tools (e.g., illustrations and sales brochures) to help the customer determine if a recommendation
          meets their needs
        </li>
        <li>Compare the existing and proposed products prior to recommending a replacement</li>
        <li>Discuss the advantages and disadvantages of the recommendation</li>
        <li>Complete all required suitability forms</li>
        <li>
          Maintain all records of the information collected from the customer and other information used in making the
          recommendations for ten (10) years after the insurance transaction is completed by AB
        </li>
        <li>Provide copies of additional suitability documentation from your customer files when requested by AB</li>
      </ul>
      <h3 id="takingTheApplicationForInsurance">Taking the Application for Insurance</h3>
      <p>
        <u>Applications must be complete and on the correct form.</u> Be sure all the information is recorded on the
        application. Since applications are filed forms approved by each state’s insurance department, you must use the
        form approved in your state. Without complete information on the correct form, AB cannot accept the application.
      </p>
      <p>
        <u>Applications must contain correct information.</u> You may assist the applicant by reading the application
        questions and recording the answers. However, you must read the questions exactly as they appear on the
        application and record the answers exactly as provided by the applicant. Never paraphrase or summarize. Also,
        you must allow the applicant to read the application and the answers to confirm that all questions have been
        answered accurately.
      </p>
      <p>
        <u>Changes to applications must be initialed by the applicant and the producer.</u> Care must be exercised in
        changing any information that is ascribed to the applicant. If information about the applicant or the coverage
        is altered (e.g., crossing out incorrect product selection), the change must be initialed and dated by the
        applicant and the producer.
      </p>
      <p>
        <u>Applications must be signed by the applicant.</u> The applicant must sign the application for insurance. You
        may not sign on behalf of the applicant, even if asked to do so by the applicant.
      </p>
      <p>
        Sometimes, the proposed insured and policyholder are not the same person. In such cases, both may need to sign
        the application.
      </p>
      <p>
        <u>Spouse Applications.</u> Often, an employee at an enrollment may wish to apply for coverage for their spouse.
        In some situations, this will require the spouse to complete and sign the application because privacy rules do
        not permit a person to authorize the release of medical records on behalf of their spouse.
      </p>
      <p>
        However, AB will accept an application signed by an employee on behalf of their spouse under the following
        circumstances:
      </p>
      <ul>
        <li>Product: Payroll life insurance up to and including $50,000. No health products are permitted.</li>
        <li>Underwriting: Limited to enrollments previously approved by AB for GI, CGI, and SI.</li>
        <li>
          States: Prohibited in Massachusetts, Missouri (individual life prohibited), Pennsylvania, and South Carolina.
          These states do not allow an employee to apply on behalf of a spouse.
        </li>
      </ul>
      <p>When permitted as described above, the:</p>
      <ul>
        <li>“Signature of Employee” is signed by the employee as the applicant and payor.</li>
        <li>“Signature of Proposed Insured” line is signed by the spouse where required.</li>
        <li>
          “Signature of Owner” line is signed by the owner if the employee is not the owner. Otherwise, the line is left
          blank.
        </li>
      </ul>
      <p>
        <u>Never permit an applicant to sign an application to be completed later.</u> The applicant may sign only after
        the application is completed. The applicant is responsible for the information contained in the application, and
        for the selection of the insurance products. This may not be delegated to the producer for completion later.
      </p>
      <p>
        <u>Applications must bear the correct date.</u> Applications may not be pre-dated or post-dated.
      </p>
      <p>
        <u>Sign only applications that you, personally, have solicited.</u> By signing an application, you are attesting
        to taking of the application. Therefore, sign only applications that you have taken from the applicant. Never
        let someone else take an application for you.
      </p>
      <p>
        <u>Record only your agent number as the soliciting agent.</u> In addition to signing applications you solicit,
        you must also record on the application your agent number. Never record another producer’s agent number as the
        soliciting agent on applications that you solicit.
      </p>
      <p>
        <u>Never permit a person who is not an agent to take an application.</u> State laws forbid unlicensed persons
        from acting as an agent. Assisting an applicant with completing an application for insurance is "acting as an
        agent."
      </p>
      <p>
        <u>Know your state’s requirements for point of sale and application-related forms.</u> Many states require, as
        part of the solicitation process, the completion or distribution of forms such as compensation disclosure
        notices, replacement notices, outlines of coverage, and illustration notices.
      </p>
      <p>
        Life illustration notices are required in most states for life policies filed as illustrated products. These
        states prohibit any life insurance illustration that includes non-guaranteed values unless the prospect receives
        a compliant illustration with required forms. To create illustrations in these states, use only enrollment
        software provided by AB (EnrollMyWay<text>&#8480;</text>) or approved by AB for illustrations. Rate cards may be
        used in these states only for payroll deduction business - they may not be used for credit union, association
        enrollments, or individual sales. Also, when using rate cards, always use the appropriate Quotation Form
        AWD3495.
      </p>
      <p>
        State regulations regarding illustrations require the company to inform producers of the expense allocation
        method used to test its products. AHL used the fully allocated expense allocation method to test its products.
        ALNY used the Generally Recognized Expense Table (“GRET”) method to test its products.
      </p>
      <p>
        <u>Don’t Make Underwriting Decisions.</u> It is the sole responsibility of AB to make underwriting decisions. No
        producer is authorized to make promises, give assurances to applicants about their insurability, or otherwise
        “bind coverage.”
      </p>
      <p>
        Conversely, producers should not refuse to complete applications. During the application process, you may learn
        that the applicant does not satisfy a condition for coverage (e.g., they do not work the required number of
        hours or have a serious health problem). You may advise the applicant of the underwriting requirement(s) and
        inquire whether they wish to continue with the application. However, do not state they are ineligible for
        coverage or refuse to submit their application. In some states, this may be considered an “adverse underwriting
        decision” requiring you to provide specific information on the reason(s) for their ineligibility and the
        applicant’s rights regarding the use and retention of information shared during the application process. In such
        a case, complete the application if requested by the applicant, accurately recording the relevant information
        for underwriting purposes. New Business will then provide the necessary notice to the applicant of their
        ineligibility for coverage.
      </p>
      <p>
        <u>Premium Payment by Producer.</u> You may pay premiums only for policies insuring yourself or immediate
        family. “Immediate family” means your spouse and dependent children only. Applications for insurance covering
        anyone other than you or your immediate family accompanied by initial premium payments drawn from your personal
        or agency’s bank account will be returned without processing.
      </p>
      <p>
        <u>Electronic Applications and Signatures.</u> AB will only accept electronic applications and/or other
        documents with electronic signatures through an approved vendor, obtained in compliance with our established
        processes and procedures. Electronic signatures include any electronic sound, symbol, or process that
        demonstrates an individual’s intent to sign an electronic document. This includes, but is not limited to,
        styluses, digital signature applications, click wraps, and other methods.
      </p>
      <h3 id="submittingTheApplication">Submitting the Application</h3>
      <p>
        Promptly submit all applications for insurance. Delay can adversely affect the right of AB to underwrite the
        application and impose liability on AB and you.
      </p>
      <p>
        When submitting the application, provide AB with any other information you have that may have a bearing on the
        underwriting process. This should include any information regarding the proposed insured's health, relevant
        circumstances, other coverages, or overall insurability. If you know or suspect that information being provided
        on the application is incorrect, incomplete, or suspicious, you are required to notify AB. This can be
        communicated in a separate letter accompanying the application.
      </p>
      <h3 id="replacementOfInsurance">Replacement of Insurance</h3>
      <p>
        The replacement of insurance coverage is often not in the best interests of the customer, and, therefore, not
        encouraged by AB. Replacement should be accomplished only when it is appropriate for the customer and then only
        after the customer is provided with the information they need to make an informed decision. As with all other
        legal requirements, specific state laws governing replacement must be followed.
      </p>
      <p>
        Special rules apply to internal replacement (replacing a policy underwritten by any Allstate affiliate with an
        AB policy). These rules are set out in the attached AB Internal Replacement Policy.
      </p>
      <h3 id="crossBorderSales">Cross-Border Sales (New York Resident Producers Only)</h3>
      <p>
        Because AHL is not an admitted insurer in New York, great care must be exercised by a New York resident producer
        who is appointed with AHL in other states. All activities related to AHL business must be accomplished outside
        of New York. For example, a New York resident producer must not, while in New York: 1) solicit AHL insurance, 2)
        recruit out-of-state subagents, 3) act as a liaison for those subagents, 4) disseminate AHL marketing materials,
        or 5) assist AHL applicants or customers.
      </p>
      <p>
        To ensure compliance, New York resident producers may not directly solicit any AHL business and must partner
        with producers not resident in New York to handle all aspects of soliciting, negotiating, and effectuating AHL
        insurance. All customer questions must be directed to the employer or to producers who are not New York resident
        producers.
      </p>
      <h3 id="targetMarket">Target Market</h3>
      <p>
        AB products are traditionally marketed to employees through employers that have a payroll deduction system
        established for employee contributions. These are corporations, partnerships, and sole proprietorships that
        generally have a minimum of ten full-time active employees who work at least 20 hours per week for the employer.
      </p>
      <p>
        Employees include any person who performs services on a full-time basis at any of the employer’s business
        establishments and is considered an employee for federal employment tax purposes. The term employee generally
        does not include retirees, or seasonal or temporary employees who work only part of the calendar year. AB will
        consider offering coverage outside of its target market, but special consideration is required. Producers should
        contact the AB Home Office before quoting any of the following non-traditional groups: Professional Employee
        Organizations, Unions, Associations, Affinity Groups, and Multiple Employer Trusts.
      </p>
      <h3 id="erisa">ERISA</h3>
      <p>
        The Employee Retirement Income Security Act of 1974 (“ERISA”), and the regulations issued pursuant to this law,
        regulate employee benefit plans. AB products are designed so that they may be offered in an employee benefit
        plan, but nothing in our products require application of ERISA. The extent to which ERISA applies is always
        determined by the actions of the employer in presenting and administering these products.
      </p>
      <h3 id="salesToSeniors">Sales to Seniors</h3>
      <p>
        Some states have adopted regulations that require special procedures relating to older applicants. Producers
        must be aware of the requirements applicable to their business, as specific requirements vary from state to
        state.
      </p>
      <h3 id="directSalesGuidelines">Direct Sales Guidelines</h3>
      <p>AB products are not available for direct sale to customers, except for ALNY Disability (“DI5NY”).</p>
      <h2 id="customerFunds">Customer Funds</h2>
      <p>The unauthorized handling of customer funds is strictly prohibited.</p>
      <h3 id="premiumPayments">Premium Payments</h3>
      <p>
        Except a first payment made pursuant to a Conditional Receipt, you may not collect any premium, except for
        worksite business for which the employer-account has authorized you to receive. In such cases, you must provide
        written proof of this authority and be approved by AB.
      </p>
      <h3 id="policyFunds">Policy Funds</h3>
      <p>
        As our producer, you must never exercise control over a customer’s assets, including policy values. Checks
        payable to a policyholder (other than a death benefit) are usually mailed directly to the policyholder. However,
        certain checks may be sent to you. If this occurs, you must promptly forward or deliver the check(s) to the
        appropriate recipient.
      </p>
      <h2 id="policyService">Policy Service</h2>
      <h3 id="policyDelivery">Policy Delivery</h3>
      <p>
        Personal delivery of the policy by the servicing producer is often helpful in developing good customer
        relations. For this reason, you may request that AB provide the policy to you for delivery. When you provide the
        policyholder the policy, it is imperative that all required documentation is delivered in accordance with state
        requirements. For example, such documentation might include a "buyer's guide" or "outline of coverage". AB may
        also require that you assist with delivery of a policy when a prior delivery attempt was unsuccessful.
      </p>
      <p>
        Delivering the policy provides you the opportunity to answer questions and insure the customer's satisfaction
        with the purchase. However, no producer is authorized to make, waive or modify any terms, conditions or
        limitations in any policy. Do not make any representations that any of the provisions of a policy are other than
        as written.
      </p>
      <h3 id="policyholderCorrespondence">Policyholder Correspondence</h3>
      <p>
        AB mails policyholder correspondence directly to the policyholder, not to a third party/producer. Upon written
        request from the policyholder, though, AB will direct correspondence (including premium notices) to a third
        party. However, AB will not accept requests to send individual premium notices to the producer.
      </p>
      <h3 id="complaints">Complaints</h3>
      <p>
        AB strives to make good faith efforts to resolve all customer complaints and disputes. Occasionally, this may
        require assistance from you.
      </p>
      <p>
        As our producer, you may receive a customer complaint directly from the customer or from a regulatory body such
        as a state’s insurance department. Examples of the type of complaints you might receive include those about the
        timing or amount of a claim payment, a policy value or lapse, or an underwriting decision. These complaints may
        require a response from you, AB, or both. Immediately report all such complaints to your Regional Office or
        AB-Complaints@allstate.com.
      </p>
      <p>
        If the complaint comes directly from the person making the complaint, it should be forwarded to
        AB-Complaints@allstate.com.
      </p>
      <p>
        If the complaint comes from a regulatory body and requires a response from you, you must also respond to the
        regulatory body in a timely manner. Never respond to any complaint on behalf of AB or make any statements on
        behalf of AB. If you are asked by AB to respond to the complaint, you must do so in accordance with the
        requirements communicated to you. Failure to cooperate timely with AB may result in suspension of commission
        payments and/or termination of your producer agreement.
      </p>
      <h3 id="claims">Claims</h3>
      <p>
        <u>You may assist claimants with claim forms.</u> You may assist claimants in filing their claims, such as by
        providing them the necessary forms and assisting with completing the documents. On occasion, you may be asked by
        AB to provide information or assist in resolving a claim.
      </p>
      <p>
        If you are sending a blank claim form to a customer but do not encrypt the email, you must clearly state that,
        for the protection of the customer’s personal information, the form should not be emailed back unless the email
        is encrypted.
      </p>
      <p>
        AB understands it is impossible to stop a customer from sending an unencrypted email with Personally
        Identifiable Information (“PII”) or Protected Health Information (“PHI”). However, this practice should be
        discouraged by only sending information securely to customers and encouraging them to reply via secure means. If
        you are sending a reply to a customer or prospective customer, even if it is just to say thank you, please
        confirm any PII/PHI in the email chain has been removed.
      </p>
      <p>
        <u>You must promptly report all claims.</u> If a claimant delivers a claim form to you, promptly submit it to
        the AB Claim Department via the agent website. Under no circumstances shall PII or PHI be transmitted
        electronically if it is unencrypted. Insurance policies often have time requirements for submitting claims.
        Failure to timely submit a claim may result in its denial. Also, laws require AB to respond to claims in a
        timely manner.
      </p>
      <p>
        <u>You may not evaluate or investigate a claim.</u> You must never assure a claimant of the outcome of any
        claim. As our producer, you may not attempt to interpret or give opinions concerning claim matters, or to assist
        a claimant in challenging a claim decision. If you believe a claim decision should be reconsidered, you should
        review the matter with AB Claim Department personnel to assure all relevant information has been evaluated.
      </p>
      <p>
        Even if you do not believe there is coverage, allow the claimant to submit the claim so that AB can make the
        decision. If you are aware of facts that may help in adjudicating the claim, advise the Claim Department.
      </p>
      <p>
        <u>Claim information is confidential.</u> You can retrieve claim summary information for your AB customers via
        the agent website. You can also obtain claim summary information from the Customer Care Center. If claim details
        are needed, you can submit an Authorization to Disclose Policy Information and Protected Health Information
        form, available on the agent website. Claim information cannot be accessed for Genelco business, or for
        partnered products where AB does not process the claims (GAP, Group PPO Dental, EyeMed Vision®). Please remember
        that pursuant to the Health Insurance Portability and Accountability Act of 1996 (“HIPAA”), information about a
        claim, including claim status, is categorized as PHI. As a business associate of AB, you are permitted to
        receive only the minimum PHI necessary to perform duties on behalf of AB. You are required to safeguard PHI in
        accordance with the terms of the Business Associate Addendum. Please be considerate of this in your dealings
        with Claim Department personnel.
      </p>
      <h2 id="compensation">Compensation</h2>
      <h3 id="noRebating">No Rebating</h3>
      <p>
        Most states prohibit insurers and producers from paying commissions or other compensation to non-agents for
        insurance sales. In these states, it is illegal for you to share, or rebate, part of your commission with other
        than licensed and appointed AB producers.
      </p>
      <h3 id="compensationCalculation">Compensation Calculation</h3>
      <p>
        You are paid commissions and service fees on premiums paid in cash to AB on insurance policies issued pursuant
        to applications procured either (i) by you, or (ii) by another producer who allocates a share of the premium
        credit to you. To procure workplace business, you must also (i) be recognized, and designated in writing, by the
        payroll account (or, in the case of group insurance, by the group policyholder) to be the agent of record for
        the workplace business, and (ii) service the workplace business to the satisfaction of AB and/or the payroll
        account (or group policyholder) as determined by AB.
      </p>
      <p>
        Typically, compensation is calculated based upon your commission level(s) as reflected in your producer
        agreement(s), using the Commission Schedule in effect at the time the business is produced, and AB rules.
        However, your compensation may be calculated differently when you are paid pursuant to:
      </p>
      <ul>
        <li>a Group Commission Agreement for a specific group insurance policy or</li>
        <li>other properly documented written agreement with AB for an account or product specific exception.</li>
      </ul>
      <p>Compensation is categorized as first year commissions, renewal commissions, and service fees.</p>
      <p>
        <u>First Year Commissions</u> are paid on insurance premium that is both:
      </p>
      <ul>
        <li>applied to the policy’s first twelve (12) months of coverage and</li>
        <li>
          received and processed by AB during the fourteen (14) months immediately following the policy’s date of issue.
        </li>
      </ul>
      <p>
        <u>Renewal Commissions</u> are paid on the following:
      </p>
      <p>
        <u>First year coverage</u>—when the insurance premium is <u>both</u>:
      </p>
      <ul>
        <li>applied to the policy’s first twelve (12) months of coverage and</li>
        <li>received and processed by AB more than fourteen (14) months after the policy’s date of issue.</li>
      </ul>
      <p>
        <u>Year 2 – 10 coverage</u>—when the insurance premium is both:
      </p>
      <ul>
        <li>applied to policy years two (2) through ten (10) and</li>
        <li>received and processed by AB less than eleven (11) years after the policy’s date of issue.</li>
      </ul>
      <p>
        <u>Service Fees</u> are paid on insurance premium that is either:
      </p>
      <ul>
        <li>applied to policy year eleven (11) or later or</li>
        <li>received and processed by AB eleven (11) or more years after the policy’s date of issue.</li>
      </ul>
      <p>
        Because they do not vest, the receiving producer must be appointed and under contract with AB as an insurance
        producer to receive service fees. Service fees terminate when your producer agreement terminates for any reason.
      </p>
      <h3 id="compensationOnPremiumRateIncreases">Compensation on Premium Rate Increases</h3>
      <p>
        Unless otherwise specifically communicated by AB, no compensation is paid to producers on premium rate
        increases.
      </p>
      <h3 id="interestOnDebtsToAB">Interest on Debts to AB</h3>
      <p>This section does not apply to R3001 Exclusive Agencies or L2000 Exclusive Financial Specialists.</p>
      <p>
        If you become indebted to AB, such as by receipt of advance commissions, AB may charge interest on your debt, in
        accordance with AB rules in effect at the time. Currently, those rules are as follows:
      </p>
      <p>
        <u>Your Debt Subject to Interest Charge.</u>
      </p>
      <p>
        Your debt subject to interest charge is the sum of the Ending Advance Balance and Ending Account Balance, as
        reflected in your monthly commission statement.
      </p>
      <p>
        <u>When Your Debt is Charged Interest.</u>
      </p>
      <p>When your debt is charged interest depends upon whether you have an active agent contract with AB.</p>
      <ol type="a">
        <li>
          <u>While you have any AB agent contract in force.</u>
          <p>
            After you have had any AB agent contract in force for at least eleven (11) months, interest is charged on
            your debt for any month during which (i) the sum of the Ending Advance Balance and Ending Account Balance is
            greater than (ii) the sum of the Pending Deferred Commissions (1st year) and Deferred Commissions (1st
            year), as reflected in your monthly commission statement, for the specific agent number.
          </p>
        </li>
        <li>
          <u>After you no longer have any AB agent contract in force.</u>
          <p>Interest is charged on your debt starting the month during which your AB agent contract is terminated.</p>
        </li>
      </ol>
      <h3 id="compensationOffsets">Compensation Offsets</h3>
      <p>
        AB may, at any time, offset against any compensation due you from any affiliates of The Allstate Corporation (to
        include AB) any debt owed by you to any affiliate of The Allstate Corporation (to include AB), and such debt
        shall be a first lien on any such compensation. As to debts you owe to AB, AB shall have the right to determine
        to which indebtedness any payment made by you, whether offset or otherwise, shall be applied.
      </p>
      <h2 id="allstateAndAllstateBenefitsNameAndLogo">Allstate and Allstate Benefits Name and Logo</h2>
      <h3 id="general">General</h3>
      <p>
        The Allstate name and logo, and that of its divisions such as AB, are invaluable marketing resources available
        to you as an insurance professional for AB. Because of their value and importance, we require all our valued
        producers to respect the proper use of these important assets.
      </p>
      <p>You may not use any Allstate brand name and/or logo without express permission from AB Marketing Department</p>
      <p>
        Please note, if you are appointed with an Allstate affiliate in addition to AB, you should consult that
        affiliate for their rules regarding the proper use of its corporate and marketing names and logos.
      </p>
      <h3 id="businessCardsAndLetterhead">Business Cards &amp; Letterhead</h3>
      <p>
        AB does not provide, nor permit its independent producers to use business cards containing the Allstate name or
        logos. To maintain consistent use of Allstate marketing identities, all producers are strictly forbidden from
        printing their own business cards or letterhead (to include fax coversheets) containing any Allstate name or
        logo.
      </p>
      <h3 id="advertisingMarketingMaterial">Advertising/Marketing Material</h3>
      <p>
        <u>Printed Sales Material.</u> AB provides producers with a wide variety of marketing materials containing the
        Allstate name and logo that can be ordered through regular supply channels.
      </p>
      <p>
        In addition, producers may use Allstate brand names and/or logos on sales material they develop contingent on
        pre-approval by the AB Marketing Department.
      </p>
      <p>
        To request sales material approval, contact the AB Marketing Department at ABMarketing@allstate.com. The AB
        Marketing Department will provide a request form to be completed and must be submitted along with the sales
        material, prior to printing or electronic production and/or posting.
      </p>
      <p>
        <u>Internet/Intranet.</u> As with printed sales material, any reference to Allstate, its affiliates and
        marketing names (to include AB), or its products must be approved by the AB Marketing Department prior to use.
      </p>
      <p>
        The use of an Allstate brand name and/or logo, its affiliates and/or marketing names (to include AB), on
        producer websites are permitted in limited circumstances, but only if <u>pre-approved</u> by the AB Marketing
        Department at ABMarketing@allstate.com. For example, identifying AHL or ALNY on a website as an insurance
        company you represent <u>may</u> be acceptable, if pre-approved by AB Marketing@allstate.com. To submit a
        request for approval, contact the AB Marketing Department at ABMarketing@allstate.com.
      </p>
      <p>
        <u>Telephone Protocol.</u> Producers are not authorized by AB to identify their offices by using Allstate name
        and/or brand, its affiliates and/or marketing names (to include AB). However, producers appointed with an
        Allstate affiliate, other than AB, should consult with that affiliate for their rules regarding telephone
        protocol.
      </p>
      <p>
        <u>Signage.</u> AB does not authorize producers to use any name or logo of Allstate, or of its affiliates, on
        permanent signage. However, producers appointed with Allstate affiliates other than AB should consult that
        affiliate for their rules regarding signage. With prior permission, producers may use the AB logo on temporary
        signage (e.g., trade show banners, enrollment banners, etc.) To submit a request for approval, contact the AB
        Marketing Department at AB-marketing@allstate.com.
      </p>
      <h2 id="privacyAndSecurityResponsibilities">Privacy and Security Responsibilities</h2>
      <p>
        AB is committed to maintaining the accuracy, confidentiality, security and privacy of personal information of
        any individual with whom AB has a business relationship. Any inappropriate or unauthorized access, misuse or
        disclosure of personal information may result in action up to and including termination of your producer
        agreement, criminal, and/or civil action.
      </p>
      <p>
        When collecting, processing, viewing, transmitting, storing or destroying personal information, authorized users
        must adhere to all procedures put in place to protect this data. This includes but is not limited to using the
        data only for business purposes and disclosing data only as required by law or to those with a valid business
        need to know.
      </p>
      <p>
        Agencies are responsible for developing, implementing and evaluating ongoing privacy education and awareness for
        all their employees, including temporary and contract workers. Privacy education and awareness should be
        conducted at least once every year.
      </p>
      <h3 id="grammLeachBlileyActOf1999">Gramm-Leach-Bliley Act of 1999 (“GLB”)</h3>
      <p>
        GLB, and similar state laws, create privacy protections for customers who have business dealings with financial
        institutions, which include insurance companies. These laws require financial institutions to protect nonpublic
        personal information and to inform customers about their policies for collecting and disclosing nonpublic
        personal information.
      </p>
      <p>
        At policy issue, and annually as required by state law, AB distributes a GLB Privacy Policy Notice, informing
        customers of AB’s privacy practices. Customer questions about this notice or AB’s privacy practices should be
        directed to AB.
      </p>
      <h3 id="healthInsurancePortabilityAndAccountabilityActOf1996">
        Health Insurance Portability and Accountability Act of 1996 (“HIPAA”)
      </h3>
      <p>
        Regulations issued by the Department of Health and Human Services pursuant to HIPAA impose, among other things,
        requirements for the use and disclosure of PHI. As an underwriter of health insurance products, these
        regulations apply to AB.
      </p>
      <p>
        Our producers, who are considered “business associates” under the regulations, share with AB responsibility for
        compliance with these requirements. A business associate is a person or entity who performs or assists in
        performing a function or activity that involves the use or disclosure of PHI. Business Associates/AB producers
        must comply with the administrative, physical, and technical safeguards for electronic PHI under the HIPAA
        Security Rule in the same manner as AB is required to comply. Business Associates/AB producers must develop and
        establish a written data security program for electronic PHI that complies with the HIPAA Security Rule.
        Additionally, Business Associates/AB producers must comply with the restrictions on use and disclosure of PHI
        contained in the HIPAA Privacy Rule.
      </p>
      <p>
        The regulations further require that the agreements we have with our producers contain language specifying this
        compliance. When a policy is issued, AB distributes a HIPAA Notice of Privacy Practices informing health
        insurance policyholders of AB’s privacy practices. AB also notifies health insurance policyholders every three
        (3) years of the availability of the privacy notice and how it may be obtained. Customer questions about this
        notice or AB’s privacy practices should be directed to AB at Privacy-AHL@allstate.com.
      </p>
      <p>
        Attachments 1 and 2 include the Business Associate Addenda for AHL and ALNY. These Addenda are a part of all
        producer agreements with AB. For additional information about your obligations to protect PHI as a business
        associate, please see Code of Federal Regulations Title 45, section 164.502.
      </p>
      <h3 id="producerResponsibilities">Producer Responsibilities</h3>
      <p>
        To help ensure AB’s compliance with HIPAA, GLB, and state privacy law requirements, all producers must comply
        with the following:
      </p>
      <ul>
        <li>
          Collect only the amount of personal information which is reasonably necessary to complete the legitimate
          business transaction for which it is collected;
        </li>
        <li>
          Use Confidential Information only for the purpose for which it was disclosed and only to carry out the
          provisions of your producer agreement;
        </li>
        <li>
          Do not disclose Confidential Information to third parties unless necessary to meet obligations under your
          producer agreement, and then only to a third party similarly bound by the same privacy standards;
        </li>
        <li>
          Adhere to the “Principle of Least Privilege” which means that authorized users must only be able to see the
          personal information required to perform their job function. Any personal information not required to perform
          a job function must be omitted or truncated;
        </li>
        <li>
          Continue to treat Confidential Information in this manner even after termination of your producer agreement;
          and
        </li>
        <li>
          Comply with all applicable privacy laws and regulations, and AB policies and procedures regarding Confidential
          Information.
        </li>
      </ul>
      <p>“Confidential Information” includes all PII and PHI about a person that:</p>
      <ul>
        <li>A person provides to obtain AB insurance;</li>
        <li>Results from an AB insurance transaction; and/or</li>
        <li>Is otherwise obtained in connection with providing AB insurance.</li>
      </ul>
      <p>
        Examples of Confidential Information include, but are not limited to: names, addresses, and dates of birth
        relating to any person; types of policies; amounts of insurance; premium amounts; policy renewal dates;
        policyholder listings, claim information; any policyholder information subject to any privacy law; and
        information identified by AB as confidential.
      </p>
      <h3 id="storageOfPersonalInformation">Storage of Personal Information</h3>
      <p>Personal information must be properly managed and retained. This means authorized users must:</p>
      <ul>
        <li>
          Only retain any paper or electronic data that contain personal information for the minimum time limit deemed
          necessary for the business purpose or as required by law.
        </li>
        <li>
          Maintain documents, files, and records that contain personal information in a locked and secure manner to
          avoid access by unauthorized persons. For example, file cabinets must be locked when not in use and computers
          must be password protected with access given only to authorized users.
        </li>
        <li>
          Comply with special records retention exceptions, including but not limited to litigation holds and regulatory
          requirements.
        </li>
      </ul>
      <h3 id="recordingTelephoneConversations">Recording Telephone Conversations</h3>
      <p>
        Recording telephone conversations may be a part of an agency business process; however, care must be taken to
        ensure that a legally compliant process is implemented. Laws which relate to the recording of telephone
        conversations vary from state to state. Some state laws require both parties to the conversation to consent;
        others require only one party to consent. Consider consulting with a qualified legal advisor to determine if
        this process is appropriate for your agency.
      </p>
      <h3 id="terminationOfEmploymentOfIndividualsWorkingOnBehalfOfAnAgency">
        Termination of Employment of Individuals Working on Behalf of an Agency
      </h3>
      <p>
        Access rights of an authorized user to AB data, systems, and facilities must be removed upon termination of the
        user’s employment, contract, or affiliation, or adjusted upon changes to the function being conducted on behalf
        of the agency. This means that agencies must:
      </p>
      <ul>
        <li>
          Immediately deactivate terminated users’ usernames and passwords to prevent access to any AB information or
          systems,
        </li>
        <li>Remove terminated users’ access to any AB information or systems, and</li>
        <li>Notify AB at AB-Licensing@allstate.com.</li>
      </ul>
      <h3 id="securityRequirementsForVendors">Security Requirements for Vendors</h3>
      <p>
        Producers, as independent contractors, are solely responsible for any agreement entered into for goods or
        services on behalf of the agency. AB does not review or approve agreements for goods and services procured by an
        agency. When entering into an agreement that could potentially involve the use, transmission, or storage of AB
        personal information, or could affect your ability to comply with these requirements, you are required to ensure
        that vendors handle this information and provide services in accordance with your obligation to comply with the
        law and the proper handling, protection and security of this information as set forth in the Privacy and
        Security Responsibilities section of this Policy.
      </p>
      <p>
        Agencies must verify that any third-party vendor or service provider selected and retained by the agency and
        with access to personal information has the capability to maintain appropriate security measures to protect the
        information consistent with state and federal laws and regulations. Agencies must also verify that any
        third-party vendor or service provider selected and retained by the agency provides services in a manner that is
        compliant with state and/or federal laws and regulations. This includes but is not limited to vendors that
        provide cleaning services, temporary employees, telephone, internet, leads and other products and services which
        are used by agencies to conduct business on behalf of Allstate. Before sharing any personal information with a
        vendor, the agency must verify that the vendor has a security program in place which complies with the policies
        set forth in the previous subsection “Storage of Personal Information”. The vendor must be contractually
        required to comply with these policies and adequately safeguard personal information.
      </p>
      <h3 id="newYorkCybersecurityRequirements">New York Cybersecurity Requirements (New York Producers)</h3>
      <p>
        Effective March 1, 2017, the New York Superintendent of Financial Services promulgated 23 NYCRR Part 500, a
        regulation establishing minimum standards to secure nonpublic information and the implementation of a
        cybersecurity program. The regulation includes a variety of requirements with different effective dates and
        applies to any company or individual licensed by the New York Department of Financial Services. The regulation
        is available at https://www.nysenate.gov/legislation/laws/CONSOLIDATED. To ensure you are complying with the
        regulation and other requirements for all the states in which you conduct business, it is recommended you
        consult your attorney for guidance. AB does not reimburse any attorney fees, as this is a matter you are
        required to comply with according to mandated state law.
      </p>
      <h3 id="usaPatriotAct">USA PATRIOT Act</h3>
      <p>
        In 2001, the Uniting and Strengthening America by Providing Appropriate Tools Required to Intercept and Obstruct
        Terrorism Act of 2001 (“the USA PATRIOT Act”) was passed with a purpose of providing a more comprehensive system
        against the fight again money laundering. Among other things, it requires that all entities defined as financial
        institutions for Bank Secrecy Act (“BSA”) purposes establish and implement an AML program. As a financial
        institution, the USA PATRIOT Act applies to Allstate Benefits and its producers. Producers play an important
        role in the prevention of money laundering.
      </p>
      <p>
        The regulations require the Anti-Money Laundering (“AML”) Program to have at minimum the following four (4)
        elements:
      </p>
      <ul>
        <li>Appointment of an AML Compliance Officer;</li>
        <li>Implementation of policies, procedures and internal controls;</li>
        <li>Ongoing training of appropriate persons on their responsibilities under the Program; and</li>
        <li>Independent testing to monitor and maintain an adequate program.</li>
      </ul>
      <p>
        The USA PATRIOT Act also requires the implementation of procedures for determining whether the person opening an
        account appears on any lists of known or suspected terrorist or terrorist organizations provided to the
        financial institution by any government agency.
      </p>
      <p>
        Remember, it is your responsibility to report any unusual or suspicious activity. To report suspicious activity:
      </p>
      <ul>
        <li>Call the AF Compliance Hotline at (866) 635-7100, or</li>
        <li>Send an email to AFSuspect@allstate.com</li>
      </ul>
      <h3 id="complianceWithCustomerAndProspectContactRulesAndRegulations">
        Compliance with Customer and Prospect Contact Rules and Regulations
      </h3>
      <p>
        When representing AB, producers must comply with each of AB’s contact compliance policies and all applicable
        state and federal laws and regulations that give customers a choice about whether they want to receive
        solicitations and other contacts by telephone, fax, text, and email.
      </p>
      <p>
        In particular, producers must adhere to the Federal Communications Commission’s (“FCC”) Telephone Consumer
        Protection Act (“TCPA”) which restricts unwanted calls, text messages and faxes. Unwanted calls, including
        robocalls and telemarketing calls, are consistently top consumer complaints the FCC receives each year. The TCPA
        places restrictions on the use of automatic dialing systems to wireless numbers (including VOIP numbers),
        artificial or prerecorded voice messages to wireless and landline numbers, text messages and faxes, without
        requisite consent.
      </p>
      <p>
        Producers are strictly prohibited from utilizing fax transmissions to solicit customers or prospects via their
        residential or business fax numbers to purchase products or services through AB. This prohibition exists
        regardless of whether an agent has obtained the recipient’s prior consent.
      </p>
      <p>
        Non-compliance with the TCPA may put your agency and AB at risk for significant fines and penalties. Agency
        owners are subject to fines from $500-$1500 per violation under the TCPA (with no cap on damages). Additional
        financial penalties exist under other federal and state rules and regulations as well as through individual or
        class action litigation. AB has adopted a zero-tolerance policy with respect to TCPA violations and any
        non-compliance may result in action up to and including termination of your producer agreement.
      </p>
      <p>
        Customer and prospect contact rules and regulations are subject to change at any time. AB will continue to make
        every effort to keep producers informed of these changes. However, you are ultimately responsible for ensuring
        that telephone calls, texts, and emails sent by your agency, or any outside vendor on your behalf, comply with
        applicable federal and state laws and AB corporate policies.
      </p>
      <h3 id="textingPolicy">Texting Policy</h3>
      <p>
        Text messages (e.g., SMS, MMS) are considered calls to wireless (mobile phone) numbers under federal and most
        state telemarketing laws. Allstate Benefits follows Allstate Insurance Company and Allstate Financial Services,
        LLC Corporate Texting Policy (“Texting Policy”) and requires all of its producers to follow this Texting Policy.
        The requirements outlined in the Texting Policy are in addition to, rather than instead of, those set forth in
        the AB’s Do Not Call (“DNC”) Policy. If any provision of the Texting Policy conflicts with AB’s DNC Policy, the
        more restrictive standard must be applied.
      </p>
      <p>
        Text messages may not be used in any manner that violates other Allstate policies and/or applicable federal or
        state laws, including those regulating advertising, PII, text message retention, telemarketing and/or marketing
        to children. The requirements in this policy apply to all methods of sending text messages to wireless phone
        numbers (e.g., short code, text broadcast technologies, peer-to-peer, emailing a message to a mobile number).
      </p>
      <p>
        Note: The above requirements were excerpts. Refer to the Attachment No. 6 Texting Policy for all requirements.
      </p>
      <h3 id="canSpamAct">CAN SPAM Act</h3>
      <p>
        The Controlling the Assault of Non-Solicited Pornography and Marketing Act of 2003 (“the CAN-SPAM Act") places
        restrictions on "commercial electronic mail messages" or commercial emails. A commercial email is an email in
        which the primary purpose is the commercial advertisement or promotion of AB and/or its products, services, or
        website.
      </p>
      <p>
        Producers have <u>no authority</u> to use commercial emails, as defined by the CAN-SPAM Act, on behalf of AB.
        Producers may, however, use commercial emails in promoting their business enterprise. As with all laws and
        regulations, though, AB requires producers engaging in such activity to comply with the requirements of the
        CAN-SPAM Act and other controlling laws. Further, any such commercial emails describing AB, or its products must
        be approved by the AB Compliance Department as sales material. AB’s review and approval of such material will be
        limited to compliance with company market conduct policies and will not be interpreted as any type of approval
        to transmit commercial emails on behalf of AB nor as a review for compliance with federal or state requirements
        affecting commercial emails.
      </p>
      <h3 id="doNotCallRegistry">Do Not Call Registry</h3>
      <p>
        Effective October 1, 2003 the National DNC Registry was implemented through the Federal Trade Commission and the
        Federal Communications Commission. These rules:
      </p>
      <ul>
        <li>
          Prohibit telephone solicitation to residential numbers that are registered with the National DNC Registry, and
        </li>
        <li>
          Require those engaged in telephone solicitation to maintain a list of persons who request not to receive calls
          made by or on behalf of the telemarketer.
        </li>
      </ul>
      <p>
        Significant fines and penalties can be assessed against violators. In addition, many states maintain their own
        DNC rules, which remain in effect, and which provide for additional fines. These federal and state rules apply
        to insurance companies and their producers.
      </p>
      <p>
        As an AB producer, you are required to comply with the AB DNC Policy, which is an attachment to this Producer
        Compliance Policy.
      </p>
      <h2 id="violations">Violations</h2>
      <p>
        Violations of this Policy, and/or its attachments, may result in sanctions to include termination of your
        appointment and contract with AB.
      </p>
      <p>
        All producers are encouraged to report violations to AB by informing your Regional Office, or the Marketing or
        Legal/Compliance Departments.
      </p>
      <h2 id="commentsAndQuestions">Comments &amp; Questions</h2>
      <p>
        AB has established a system of supervision and review to help achieve compliance with these principles and
        policies that includes:
      </p>
      <ul>
        <li>policies and procedures designed to comply and monitor compliance,</li>
        <li>supervision of our market activities to monitor compliance, and</li>
        <li>compliance training for employees and producers.</li>
        <li>
          AB seeks to constantly improve our sales and marketing practices. Your suggestions and comments are integral
          to this and are always welcomed. Please contact your Regional Office, the Marketing or the Legal/Product
          Support Departments if you have any questions.
        </li>
      </ul>
      <h3 id="attachments">Attachments</h3>
      <List className="producer-compliance-policy__attachments-list">
        {matchAsync(attachments, {
          Loading: () => <Loading />,
          Loaded: attachments => (
            <>
              {attachments
                .sort(
                  (link1, link2) =>
                    getAttachmentSortOrderByName(getFileNameWithoutExtension(link1.rel!)) -
                    getAttachmentSortOrderByName(getFileNameWithoutExtension(link2.rel!))
                )
                .map(({rel, href}, index) => (
                  <ListItem key={rel!}>
                    <Grid
                      container
                      justify="space-between"
                      alignItems="center"
                      className="producer-compliance-policy__attachments-list__item"
                    >
                      <Grid item alignItems="center">
                        {`${index + 1}. ${getFileNameWithoutExtension(rel!)}`}
                      </Grid>
                      <Grid item>
                        <FileDownloadButton
                          downloadedFileName={rel!}
                          displayTooltip={false}
                          relativeFileLocation={
                            href! +
                            addFilterParameters({
                              encryptedIdentifier: selectedAgency.encryptedIdentifier,
                            })
                          }
                        >
                          Download
                        </FileDownloadButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
            </>
          ),
          Conflict: error => <ErrorMessage errorMessage={error.message} />,
          ServerError: error => <ErrorMessage errorMessage={error.message} />,
          Unauthorized: error => <ErrorMessage errorMessage={error.message} />,
        })}
      </List>
    </div>
  );
};

export default ProducerCompliancePolicy;
